import React, { useState, useEffect } from 'react';
import API_BASE from '../../utils/api';
import { useAuth } from '../../auth/useAuth';

function formatCurrency(cents) {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Confirms adding a paid seat. Nothing is charged today: the seat is added to
// the owner's subscription and the rest of the current billing period is
// prorated onto their next invoice.
export default function AddSeatModal({
  invitedEmail,
  preview: initialPreview,
  onClose,
  onSuccess,
}) {
  const { auth } = useAuth();

  const [preview, setPreview] = useState(initialPreview || null);
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialPreview) return;
    async function fetchPreview() {
      try {
        const res = await fetch(`${API_BASE}/api/stripe/preview-seat`, {
          headers: { Authorization: `Bearer ${auth.idToken}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load pricing');
        setPreview(data);
      } catch (err) {
        setPreviewError(err.message);
      }
    }
    fetchPreview();
  }, [auth.idToken, initialPreview]);

  async function handleConfirm(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/stripe/add-seat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.idToken}`,
        },
        body: JSON.stringify({ invitedEmail }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add seat');

      onSuccess(data);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  const intervalLabel = preview?.interval === 'year' ? 'year' : 'month';
  const nextInvoice = preview?.nextInvoiceDate ? ` on ${formatDate(preview.nextInvoiceDate)}` : '';

  return (
    <div className="team-modal-overlay" onClick={loading ? undefined : onClose}>
      <div className="team-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Add a paid seat</h3>

        <p className="seat-modal-desc">
          This adds a paid seat for <strong>{invitedEmail}</strong> to your subscription. You can
          remove it anytime from My Team, and any unused time is credited back.
        </p>

        <div className="seat-price-row">
          {previewError ? (
            <span className="seat-price-error">{previewError}</span>
          ) : preview ? (
            <>
              <span className="seat-price-amount">{formatCurrency(preview.unitAmount)}</span>
              <span className="seat-price-label">
                per {intervalLabel}, renewing with your subscription
              </span>
            </>
          ) : (
            <span className="seat-price-loading">Calculating price…</span>
          )}
        </div>

        {preview && !previewError && (
          <p className="seat-modal-desc">
            Nothing is charged today. Your next invoice{nextInvoice} will include{' '}
            {preview.prorationAmount != null
              ? `${formatCurrency(preview.prorationAmount)} for`
              : 'a prorated charge for'}{' '}
            the rest of your current billing period.
          </p>
        )}

        <form onSubmit={handleConfirm}>
          {error && <p className="seat-modal-error">{error}</p>}

          <div className="form-actions">
            <button type="button" className="team-cancel-btn" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              className="team-save-btn"
              disabled={loading || !preview || !!previewError}
            >
              {loading ? 'Adding…' : 'Add Seat'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
